import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Grid,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import api from '../services/api';
import AddDeliveryPhotos from './AddDeliveryPhotos';

const deliveryPhotoLocationLabel = (source) => {
  const labels = {
    client: 'Device GPS at upload',
    exif: 'Embedded in photo (EXIF)',
    completion_site: 'Job completion coordinates',
    unknown: 'Not recorded',
  };
  return labels[source] || source || '—';
};

/** Normalize delivery-photo URLs for authenticated fetch (axios baseURL includes /api). */
export function deliveryPhotoApiPath(imageUrl) {
  if (!imageUrl) return null;

  const apiJobsMatch = imageUrl.match(/\/api(\/jobs\/[^?#]+\/delivery-photos\/[^?#]+\/?)/i);
  if (apiJobsMatch) return apiJobsMatch[1];

  const jobsMatch = imageUrl.match(/(\/jobs\/[^?#]+\/delivery-photos\/[^?#]+\/?)/i);
  if (jobsMatch) return jobsMatch[1];

  if (imageUrl.startsWith('/api/')) return imageUrl.slice(4);
  if (imageUrl.startsWith('/jobs/')) return imageUrl;
  return null;
}

function mediaOrigin() {
  return (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
}

/** Resolve how to fetch a delivery photo (API route vs direct media/S3 URL). */
export function resolvePhotoFetchTarget(imageUrl) {
  if (!imageUrl) return null;

  const apiPath = deliveryPhotoApiPath(imageUrl);
  if (apiPath) return { type: 'api', path: apiPath };

  if (imageUrl.startsWith('/media/') || imageUrl.startsWith('media/')) {
    const mediaPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    return { type: 'absolute', url: `${mediaOrigin()}${mediaPath}` };
  }

  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return { type: 'absolute', url: imageUrl };
  }

  return null;
}

function AuthenticatedDeliveryPhoto({ imageUrl, alt, height = 200 }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const target = resolvePhotoFetchTarget(imageUrl);
    if (!target) {
      setFailed(true);
      setLoading(false);
      return undefined;
    }

    let blobUrl;
    setLoading(true);
    setFailed(false);
    setSrc(null);

    const request =
      target.type === 'api'
        ? api.get(target.path, { responseType: 'blob' })
        : api.get(target.url, { responseType: 'blob' });

    request
      .then((res) => {
        blobUrl = URL.createObjectURL(res.data);
        setSrc(blobUrl);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));

    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [imageUrl]);

  if (loading) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (failed || !src) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Image unavailable
        </Typography>
      </Box>
    );
  }

  return (
    <Link href={src} target="_blank" rel="noopener noreferrer" underline="none">
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: '100%',
          height,
          objectFit: 'cover',
          display: 'block',
          bgcolor: 'action.hover',
        }}
      />
    </Link>
  );
}

/**
 * Grid of geotagged proof-of-delivery photos from delivery_completion.photos.
 */
export default function JobDeliveryPhotos({
  photos,
  title = 'Delivery photos',
  jobId = null,
  canAdd = false,
  onPhotosAdded = null,
}) {
  const list = Array.isArray(photos) ? photos : [];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No delivery photos were uploaded for this job.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Proof-of-delivery images from the driver. Coordinates are read from photo EXIF when
            available; otherwise your device GPS at upload time is used.
          </Typography>
          <Grid container spacing={2}>
            {list.map((photo) => (
              <Grid item xs={12} sm={6} md={4} key={photo.id}>
                <Paper variant="outlined" sx={{ overflow: 'hidden', height: '100%' }}>
                  <AuthenticatedDeliveryPhoto
                    imageUrl={photo.image_url}
                    alt="Delivery proof"
                  />
                  <Box sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      <strong>Location source:</strong>{' '}
                      {deliveryPhotoLocationLabel(photo.location_source)}
                    </Typography>
                    {photo.latitude != null && photo.longitude != null ? (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        <strong>GPS:</strong> {parseFloat(photo.latitude).toFixed(6)},{' '}
                        {parseFloat(photo.longitude).toFixed(6)}{' '}
                        <Link
                          href={`https://www.google.com/maps?q=${encodeURIComponent(`${photo.latitude},${photo.longitude}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontSize: 'inherit', ml: 0.5 }}
                        >
                          Map
                        </Link>
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        No coordinates stored for this image.
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {canAdd && jobId && (
        <AddDeliveryPhotos
          jobId={jobId}
          existingPhotoCount={list.length}
          onUploaded={onPhotosAdded}
        />
      )}
    </Box>
  );
}
